/* Time and date helpers for the schedule UI.

   Convention: backend timestamps are wall-clock ISO strings ("YYYY-MM-DDTHH:MM:SSZ").
   The trailing Z is a storage convention, NOT a UTC offset — the entire app
   writes and groups times as local wall-clock with a fixed Z suffix. So all
   *display* formatters slice the string directly. Only *arithmetic* helpers
   (addMinutes, diffMin, snap15) use real Date math, which is offset-stable
   because all timestamps share the same Z offset. */

export function parseUtc(iso) {
  if (!iso) return null;
  if (iso instanceof Date) return iso;
  return new Date(iso);
}

export function fmtTime(iso) {
  if (!iso) return "—";
  if (iso instanceof Date) {
    return `${String(iso.getUTCHours()).padStart(2, "0")}:${String(iso.getUTCMinutes()).padStart(2, "0")}`;
  }
  const s = String(iso);
  // Expect "YYYY-MM-DDTHH:MM..." — slice the wall-clock hours+minutes.
  if (s.length >= 16 && s[10] === "T") return s.slice(11, 16);
  // Fallback for unusual inputs.
  const d = new Date(s);
  if (Number.isNaN(d.getTime())) return "—";
  return `${String(d.getUTCHours()).padStart(2, "0")}:${String(d.getUTCMinutes()).padStart(2, "0")}`;
}

export function fmtDate(iso) {
  if (!iso) return "—";
  const s = iso instanceof Date ? iso.toISOString() : String(iso);
  // Read the date portion of the wall-clock ISO directly — don't let browser
  // TZ shift the day.
  if (s.length >= 10) {
    const [y, m, d] = s.slice(0, 10).split("-").map(Number);
    if (y && m && d) {
      const local = new Date(y, m - 1, d); // local Date for weekday/month names
      return local.toLocaleDateString("en-GB", { weekday: "short", day: "2-digit", month: "short" });
    }
  }
  return "—";
}

export function fmtDateKey(iso) {
  if (!iso) return "";
  const s = iso instanceof Date ? iso.toISOString() : String(iso);
  if (s.length >= 10) return s.slice(0, 10);
  return "";
}

export function fmtDuration(min) {
  if (min == null || min < 0) return "—";
  const h = Math.floor(min / 60);
  const m = min % 60;
  if (h === 0) return `${m}m`;
  if (m === 0) return `${h}h`;
  return `${h}h ${m}m`;
}

export function diffMin(a, b) {
  const da = parseUtc(a);
  const db = parseUtc(b);
  if (!da || !db) return 0;
  return Math.round((db.getTime() - da.getTime()) / 60000);
}

export function addMinutes(iso, mins) {
  const d = parseUtc(iso);
  if (!d) return null;
  return new Date(d.getTime() + mins * 60000);
}

export function isoNoMs(d) {
  return new Date(d).toISOString();
}
